/**
 * Auf-/Zuklappen in der **Vorschau** – dieselben Bereiche wie im Quelltext.
 *
 * Links am Rand jeder faltbaren Überschrift und jedes Listenpunkts mit
 * Unterpunkten sitzt ein Dreieck: nach oben = aufgeklappt, zur Seite =
 * zugeklappt. Ein Klick faltet **auch den Quelltext** (und umgekehrt) – der
 * Faltzustand lebt nur im CodeMirror-Editor, diese Datei zeichnet ihn bloß
 * nach. Am Text ändert sich dabei nichts, gespeichert wird immer alles.
 *
 * `:::`-Container bleiben außen vor: in der Vorschau sind sie schon eigene
 * Boxen, dort ergibt Falten keinen Sinn. `$$`-Formelblöcke sind dagegen
 * auch hier faltbar (siehe folding.ts) – anders als bei Überschrift/
 * Listenpunkt gibt es dort keinen "Titel", der stehen bleibt: zugeklappt
 * verschwindet die ganze Formel bis auf das Dreieck.
 */

import type { CodeEditor } from "./codeEditor";

const TRIANGLE = `<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M2 4.5h8L6 9z" fill="currentColor"/></svg>`;

const FOLDABLE_TAGS = new Set(["H1", "H2", "H3", "H4", "H5", "H6", "LI"]);

/**
 * Elemente, die in der Vorschau ein Dreieck bekommen können: Überschriften,
 * Listenpunkte und Formelabsätze. Von Hand statt per `querySelectorAll`, weil
 * so das Innere jeder Formel übersprungen wird – KaTeX macht den Großteil
 * aller Knoten der Vorschau aus, und das hier läuft nach jedem Tastendruck.
 */
function foldables(root: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) =>
      (n as Element).classList.contains("katex") || (n as Element).tagName === "svg"
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  for (let n = walker.nextNode() as HTMLElement | null; n; n = walker.nextNode() as HTMLElement | null) {
    if (FOLDABLE_TAGS.has(n.tagName)) {
      if (n.hasAttribute("data-line")) out.push(n);
    } else if (n.classList.contains("katex-display")) {
      const p = n.parentElement;
      if (p?.tagName === "P" && p.hasAttribute("data-line")) out.push(p);
    }
  }
  return out;
}

function lineOf(el: Element): number | null {
  const v = (el as HTMLElement).dataset?.line;
  if (v == null) return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

export interface PreviewFold {
  /** Nach jedem Neurendern der Vorschau aufrufen. */
  apply(): void;
  destroy(): void;
}

export function attachPreviewFold(root: HTMLElement, editor: CodeEditor): PreviewFold {
  const onClick = (e: MouseEvent) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(".md-fold-toggle");
    if (!t || !root.contains(t)) return;
    e.preventDefault();
    e.stopPropagation();
    const line = parseInt(t.dataset.foldLine ?? "", 10);
    if (!Number.isNaN(line)) editor.toggleFold(line);
  };
  root.addEventListener("click", onClick);

  /**
   * Gleicht Dreiecke und verborgene Bereiche mit dem Faltzustand ab. Läuft
   * nach jedem Tastendruck – deshalb wird nur angefasst, was wirklich anders
   * ist: die Vorschau tauscht unveränderte Blöcke nicht mehr aus
   * (markdown/reuse.ts), und jedes unnötige Ab- und Wiederanhängen eines
   * Knopfs würde genau diese Blöcke neu layouten lassen.
   */
  function apply(): void {
    const folded = editor.foldedLines();
    const foldable = new Set<Element>();
    const toggles = new Set<Element>();
    const hidden = new Set<Element>();

    for (const el of foldables(root)) {
      const line = lineOf(el);
      if (line == null) continue;
      const span = editor.foldSpan(line);
      // Container und Codeblöcke lassen sich nur im Quelltext falten –
      // in der Vorschau sind sie schon fertige Boxen bzw. Diagramme.
      if (!span || span.kind === "container" || span.kind === "fence") continue;

      const isFolded = folded.has(line);
      foldable.add(el);
      el.classList.add("md-foldable");
      el.classList.toggle("md-folded", isFolded);

      let t = el.querySelector<HTMLButtonElement>(":scope > .md-fold-toggle");
      if (!t) {
        t = document.createElement("button");
        t.type = "button";
        t.innerHTML = TRIANGLE;
        el.prepend(t);
      }
      const cls = "md-fold-toggle" + (isFolded ? " is-closed" : "");
      if (t.className !== cls) t.className = cls;
      const title = isFolded ? "Aufklappen" : "Zuklappen";
      if (t.title !== title) t.title = title;
      if (t.dataset.foldLine !== String(line)) t.dataset.foldLine = String(line);
      toggles.add(t);

      if (!isFolded) continue;
      if (el.tagName === "LI") hideChildren(el, line, span.to, hidden);
      else if (span.kind === "math") hideOwnContent(el, hidden);
      else hideFollowing(el, span.to, hidden);
    }

    for (const t of Array.from(root.querySelectorAll(".md-fold-toggle"))) if (!toggles.has(t)) t.remove();
    for (const el of Array.from(root.querySelectorAll(".md-foldable"))) {
      if (!foldable.has(el)) el.classList.remove("md-foldable", "md-folded");
    }
    for (const el of Array.from(root.querySelectorAll(".md-fold-hidden"))) {
      if (!hidden.has(el)) el.classList.remove("md-fold-hidden");
    }
    for (const el of hidden) el.classList.add("md-fold-hidden");
  }

  /**
   * Formelblock: die Formel ist der ganze Absatz, es gibt kein Stück "Titel",
   * das (wie bei Überschrift/Listenpunkt) stehen bleibt – zugeklappt
   * verschwindet der Inhalt bis auf das Dreieck selbst.
   */
  function hideOwnContent(el: Element, out: Set<Element>): void {
    for (const c of Array.from(el.children)) {
      if (!c.classList.contains("md-fold-toggle")) out.add(c);
    }
  }

  /** Alles nach der Überschrift verbergen, bis eine Zeile hinter dem Bereich. */
  function hideFollowing(el: Element, to: number, out: Set<Element>): void {
    let n = el.nextElementSibling;
    while (n) {
      const ln = lineOf(n);
      if (ln != null && ln > to) break;
      out.add(n);
      n = n.nextElementSibling;
    }
  }

  /** Unterpunkte eines Listenpunkts verbergen – der Punkt selbst bleibt. */
  function hideChildren(li: Element, from: number, to: number, out: Set<Element>): void {
    for (const c of Array.from(li.children)) {
      if (c.classList.contains("md-fold-toggle")) continue;
      const ln = lineOf(c);
      if (ln == null || ln <= from) continue;
      if (ln > to) break;
      out.add(c);
    }
  }

  editor.onFoldChange(apply);

  return {
    apply,
    destroy() {
      root.removeEventListener("click", onClick);
    },
  };
}
